import React, { useEffect, useState, useRef } from 'react';
import { Alert, Button, Container, Stack, Text, Title, Paper, Group } from '@mantine/core';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { IconCheck, IconX, IconAlertCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { EmailProblemsApi } from '../../../global/apis/EmailProblemsApi/EmailProblemsApi';

export enum VerificationState {
    Loading = 'loading',
    Success = 'success',
    Error = 'error',
    InvalidToken = 'invalid_token'
}

const EmailVerification: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { lang } = useParams<{ lang?: string }>();
    const { t, i18n } = useTranslation();
    const [verificationState, setVerificationState] = useState<VerificationState>(VerificationState.Loading);
    const [message, setMessage] = useState<string>('');
    const hasCalledVerify = useRef(false);

    const token = searchParams.get('token');

    useEffect(() => {
        // Set language if provided in URL parameter
        if (lang && ['en', 'fr'].includes(lang)) 
            i18n.changeLanguage(lang);
        
    }, [lang, i18n]);

    useEffect(() => {
        // Prevent double execution in React.StrictMode
        if (hasCalledVerify.current) return;
        hasCalledVerify.current = true;

        const verifyEmail = async () => {
            if (!token) {
                setVerificationState(VerificationState.InvalidToken);
                setMessage(t('emailVerification.invalidToken'));
                return;
            }

            try {
                const response = await EmailProblemsApi.verifyEmail(token);

                if (response.success) {
                    setVerificationState(VerificationState.Success);
                    setMessage(response.message || t('emailVerification.success'));
                } else {
                    setVerificationState(VerificationState.Error);
                    setMessage(response.message || t('emailVerification.error'));
                }
            } catch (error: any) {
                console.error('Error verifying email:', error);
                setVerificationState(VerificationState.Error);
                
                // Try to extract error message from the response
                let errorMessage = t('emailVerification.error');
                if (error?.response?.data?.message) 
                    errorMessage = error.response.data.message;
                 else if (error?.message) 
                    errorMessage = error.message;
                
                setMessage(errorMessage);
            }
        };
        
        verifyEmail();
    }, [token, t]);

    const handleGoToLogin = () => {
        // Navigate to login with the current language if available
        if (lang && ['en', 'fr'].includes(lang)) 
            navigate(`/${lang}/login`);
         else 
            navigate('/login');
        
    };

    const renderContent = () => {
        switch (verificationState) {
            case VerificationState.Loading:
                return (
                    <Alert icon={<IconAlertCircle size={16} />} color="blue">
                        {t('emailVerification.verifying')}
                    </Alert>
                );

            case VerificationState.Success:
                return (
                    <Alert icon={<IconCheck size={16} />} color="green">
                        <Text fw={500}>{t('emailVerification.successTitle')}</Text>
                        <Text size="sm" mt="xs">{message}</Text>
                    </Alert>
                );

            case VerificationState.Error:
            case VerificationState.InvalidToken:
                return (
                    <Alert icon={<IconX size={16} />} color="red">
                        <Text fw={500}>{t('emailVerification.errorTitle')}</Text>
                        <Text size="sm" mt="xs">{message}</Text>
                    </Alert>
                );

            default:
                return null;
        }
    };

    return (
        <Container size="sm" style={{ paddingTop: '4rem' }}>
            <Paper p="xl" shadow="md" radius="md">
                <Stack gap="lg" align="center">
                    <div style={{ textAlign: 'center' }}>
                        <Title order={2} mb="sm">
                            {t('emailVerification.title')}
                        </Title>
                        <Text c="dimmed">
                            {t('emailVerification.description')}
                        </Text>
                    </div>

                    {renderContent()}

                    {verificationState !== VerificationState.Loading && (
                        <Group justify="center" mt="lg">
                            <Button onClick={handleGoToLogin} variant="filled">
                                {t('emailVerification.goToLogin')}
                            </Button>
                        </Group>
                    )}
                </Stack>
            </Paper>
        </Container>
    );
};

export default EmailVerification; 